import { fetcher } from '@/lib/api';

export interface Client {
  id: string;
  email: string;
  nome_completo: string;
  telefone: string;
  cpf: string;
  profissao?: string;
  
  // Endereço integrado
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  
  // Controle
  is_active: boolean;
  approved: boolean;
  aceitou_termos: boolean;
  data_aceitacao_termos?: string;
  data_cadastro: string;
  contador_cancelamentos: number;
  login_tentativas: number;
  bloqueio_ate?: string;
}

export interface CreateClientData {
  email: string;
  password: string;
  nome_completo: string;
  telefone: string;
  cpf: string;
  profissao?: string;
  
  // Endereço
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  
  // Controle
  is_active?: boolean;
  approved?: boolean;
}

export interface UpdateClientData {
  email?: string;
  nome_completo?: string;
  telefone?: string;
  cpf?: string;
  profissao?: string;
  
  // Endereço
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  
  // Controle
  is_active?: boolean;
  approved?: boolean;
}

export interface ClientListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive';
  approved?: 'true' | 'false';
  blocked?: 'true' | 'false';
}

export interface ClientStats {
  total: number;
  active: number;
  approved: number;
  blocked: number;
  pending: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface ViaCepResponse {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
}

// Agora usando a API configurada que já tem autenticação automática

export const clientService = {
  // Listar clientes com filtros e paginação
  async listClients(query: ClientListQuery = {}): Promise<PaginatedResponse<Client>> {
    const searchParams = new URLSearchParams();
    if (query.page) searchParams.append('page', query.page.toString());
    if (query.limit) searchParams.append('limit', query.limit.toString());
    if (query.search) searchParams.append('search', query.search);
    if (query.status) searchParams.append('status', query.status);
    if (query.approved) searchParams.append('approved', query.approved);
    if (query.blocked) searchParams.append('blocked', query.blocked);
    const endpoint = `/api/clients${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return await fetcher<PaginatedResponse<Client>>({ method: 'get', url: endpoint });
  },

  // Obter estatísticas de clientes
  async getClientStats(): Promise<ClientStats> {
    const result = await fetcher<{ data: ClientStats }>({ method: 'get', url: '/api/clients/stats' });
    return result.data;
  },

  // Buscar cliente por ID
  async getClientById(id: string): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'get', url: `/api/clients/${id}` });
    return result.data;
  },

  // Criar novo cliente
  async createClient(data: CreateClientData): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'post', url: '/api/clients', data });
    return result.data;
  },

  // Atualizar cliente
  async updateClient(id: string, data: UpdateClientData): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'put', url: `/api/clients/${id}`, data });
    return result.data;
  },

  // Redefinir senha do cliente
  async resetClientPassword(id: string, newPassword: string): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'patch', url: `/api/clients/${id}/reset-password`, data: { newPassword } });
    return result.data;
  },

  // Aprovar/desaprovar cliente
  async approveClient(id: string, approved: boolean): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'patch', url: `/api/clients/${id}/approve`, data: { approved } });
    return result.data;
  },

  // Ativar/desativar cliente
  async activateClient(id: string, isActive: boolean): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'patch', url: `/api/clients/${id}/activate`, data: { is_active: isActive } });
    return result.data;
  },

  // Bloquear cliente
  async blockClient(id: string, bloqueioAte?: string): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'patch', url: `/api/clients/${id}/block`, data: { bloqueio_ate: bloqueioAte } });
    return result.data;
  },

  // Desbloquear cliente
  async unblockClient(id: string): Promise<Client> {
    const result = await fetcher<{ data: Client }>({ method: 'patch', url: `/api/clients/${id}/unblock` });
    return result.data;
  },

  // Remover cliente
  async deleteClient(id: string): Promise<void> {
    await fetcher<void>({ method: 'delete', url: `/api/clients/${id}` });
  },

  // Buscar endereço por CEP
  async getAddressByCep(cep: string): Promise<ViaCepResponse> {
    const result = await fetcher<{ data: ViaCepResponse }>({ method: 'get', url: `/api/clients/cep/${cep}` });
    return result.data;
  },
}; 
